import type { ComponentType } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { WorkspaceAvatarGroup } from "@components/avatar/WorkspaceAvatarGroup";
import {
    ActionsCell,
    CellText,
    type DataGridCellProps,
} from "@design-system/DataGrid";
import { Menu } from "@design-system/Menu/Menu.lib";
import type { ServiceTemplateScheduleRow } from "../serviceTemplateSchedulesViewConfig";
import {
    getGroupClients,
    getGroupEntities,
    type ScheduleListLookups,
} from "./scheduleListValues";

/** What a schedule's row actions do, by schedule or template id. */
export interface ServiceTemplateScheduleCallbacks {
    onDeleteSchedule: (scheduleId: string) => void;
    onPauseSchedule: (scheduleId: string) => void;
    onResumeSchedule: (scheduleId: string) => void;
    onGoToTemplate: (templateId: string) => void;
    onOpenSchedule: (scheduleId: string) => void;
}

type ScheduleListCell = ComponentType<
    DataGridCellProps<ServiceTemplateScheduleRow>
>;

const GROUP_AVATAR_MAX = 3;

/** A group's own text, for columns its schedules share; blank when there is none. */
export const buildTextSummary = (
    read: (row: ServiceTemplateScheduleRow) => string | null,
): ScheduleListCell => {
    const ScheduleTextSummary: ScheduleListCell = ({ context, meta }) => {
        const text = read(context.row.original);
        return text === null ? null : (
            <CellText text={text} overflow={meta.overflow} />
        );
    };
    return ScheduleTextSummary;
};

/** A group's clients as a stack and "N clients", counting clients not yet resolved too. */
export const buildClientSummary = (
    lookups: ScheduleListLookups,
): ScheduleListCell => {
    const ScheduleClientSummary: ScheduleListCell = ({ context, meta }) => {
        const row = context.row.original;
        if (row.rowType !== "group") {
            return null;
        }
        const { workspaces, clientCount } = getGroupClients({ row, lookups });
        return (
            <WorkspaceAvatarGroup
                workspaces={workspaces}
                max={GROUP_AVATAR_MAX}
                label={
                    <CellText
                        text={pluralizeWithCount("client", clientCount)}
                        overflow={meta.overflow}
                    />
                }
            />
        );
    };
    return ScheduleClientSummary;
};

/** A group's entities as a stack and "N entities". */
export const buildEntitySummary = (
    lookups: ScheduleListLookups,
): ScheduleListCell => {
    const ScheduleEntitySummary: ScheduleListCell = ({ context, meta }) => {
        const row = context.row.original;
        if (row.rowType !== "group") {
            return null;
        }
        const entities = getGroupEntities({ row, lookups });
        return (
            <EntityAvatarGroup
                entities={entities}
                max={GROUP_AVATAR_MAX}
                label={
                    <CellText
                        text={pluralizeWithCount("entity", entities.length)}
                        overflow={meta.overflow}
                    />
                }
            />
        );
    };
    return ScheduleEntitySummary;
};

interface ScheduleActionsInputs {
    lookups: ScheduleListLookups;
    callbacks: ServiceTemplateScheduleCallbacks;
    isUpdatingStatus: boolean;
}

/**
 * A schedule's Open and its menu: Pause or Resume (not once its template is
 * archived), Go to template, and Delete. A group's menu has only Go to
 * template, since every schedule in it shares the template.
 */
export const buildScheduleActionsCell = ({
    lookups,
    callbacks,
    isUpdatingStatus,
}: ScheduleActionsInputs): ScheduleListCell => {
    const ScheduleActionsCell: ScheduleListCell = ({ context }) => {
        const row = context.row.original;
        const goToTemplateItem = (
            <Menu.Item onClick={() => callbacks.onGoToTemplate(row.templateId)}>
                Go to template
            </Menu.Item>
        );
        if (row.rowType === "group") {
            return (
                <ActionsCell>
                    <ActionsCell.Menu aria-label="Schedule group actions">
                        {goToTemplateItem}
                    </ActionsCell.Menu>
                </ActionsCell>
            );
        }
        const scheduleId = row.id;
        const isPaused = row.status === ServiceTemplateRecurrenceStatus.PAUSED;
        const isArchived = lookups.archivedTemplateIds.has(row.templateId);
        return (
            <ActionsCell>
                <ActionsCell.Button
                    variant="primary"
                    onClick={() => callbacks.onOpenSchedule(scheduleId)}
                >
                    Open
                </ActionsCell.Button>
                <ActionsCell.Menu aria-label="Schedule actions">
                    {!isArchived && (
                        <Menu.Item
                            disabled={isUpdatingStatus}
                            onClick={() =>
                                isPaused
                                    ? callbacks.onResumeSchedule(scheduleId)
                                    : callbacks.onPauseSchedule(scheduleId)
                            }
                        >
                            {isPaused ? "Resume" : "Pause"}
                        </Menu.Item>
                    )}
                    {goToTemplateItem}
                    <Menu.Item
                        destructive
                        onClick={() => callbacks.onDeleteSchedule(scheduleId)}
                    >
                        Delete
                    </Menu.Item>
                </ActionsCell.Menu>
            </ActionsCell>
        );
    };
    return ScheduleActionsCell;
};
